import { useEffect, useState } from 'react';
import { ShieldCheckIcon, SlidersHorizontalIcon, UserCircleIcon } from '@phosphor-icons/react';

import { PreferencesSection } from '@/features/settings/sections/PreferencesSection';
import { ProfileSection } from '@/features/settings/sections/ProfileSection';
import { SecuritySection } from '@/features/settings/sections/SecuritySection';
import { Modal } from '@/ui/Modal';
import { TabBar, type TabItem } from '@/ui/Tabs';

type AccountTab = 'profile' | 'security' | 'preferences';

const TABS: TabItem<AccountTab>[] = [
  { value: 'profile', label: 'Profile', icon: <UserCircleIcon size={15} /> },
  { value: 'security', label: 'Security', icon: <ShieldCheckIcon size={15} /> },
  { value: 'preferences', label: 'Preferences', icon: <SlidersHorizontalIcon size={15} /> },
];

interface AccountSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/** Per-user account settings (profile, security, preferences) in a popup, opened from the sidebar user menu. */
export function AccountSettingsModal({ isOpen, onClose }: AccountSettingsModalProps) {
  const [tab, setTab] = useState<AccountTab>('profile');

  // Always land on Profile whenever the popup opens.
  useEffect(() => {
    if (isOpen) setTab('profile');
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Account settings"
      description="Your personal profile, security, and preferences — applied across every team you belong to."
      maxWidth="max-w-2xl"
    >
      <div className="flex min-h-0 flex-col gap-5 px-6 pb-6">
        <TabBar value={tab} onChange={setTab} tabs={TABS} ariaLabel="Account settings sections" />

        {/* Fixed height so the popup doesn't resize between tabs */}
        <div className="flex h-[60vh] flex-col gap-10 overflow-y-auto">
          {tab === 'profile' && <ProfileSection />}
          {tab === 'security' && <SecuritySection />}
          {tab === 'preferences' && <PreferencesSection />}
        </div>
      </div>
    </Modal>
  );
}
